import axios from '../../../api-client.ts';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DisputeChatInner from '../../../../_mesomb/partials/components/ChatWidget/DisputeChatInner.tsx';
import {SecureDispute, SecureDisputeTimelineItem} from "../../../../types";

interface Props {
  token: string;
  dispute: SecureDispute;
}

function PublicDisputeHistoryWidget({token, dispute: _dispute}: Props) {
  const {t} = useTranslation();
  const [mode, setMode] = useState<'message' | 'evidence'>('message');
  const [message, setMessage] = useState('');
  const [evidenceTitle, setEvidenceTitle] = useState('');
  const [evidenceComment, setEvidenceComment] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [fileId, setFileId] = useState('')
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<SecureDisputeTimelineItem[]>([])
  const [isLoading, setIsLoading] = useState(false);

  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get<SecureDisputeTimelineItem[]>(
        `/secure-pay/${token}/dispute/timeline/`
      );
      setTimeline(response.data);
    } catch {
      setTimeline([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const resetComposer = () => {
    setMessage('');
    setEvidenceTitle('');
    setEvidenceComment('');
    setEvidenceUrl('');
  };

  const submit = async () => {
    if (mode === 'message') {
      const content = message.trim();
      if (!content) {
        setError(t('Message cannot be empty', {defaultValue: 'Message cannot be empty'}));
        return;
      }
    } else {
      const title = evidenceTitle.trim();
      const url = evidenceUrl.trim();
      if (!title) {
        setError(
          t('Evidence title is required', {
            defaultValue: 'Evidence title is required',
          })
        );
        return;
      }
      if (!url && !fileId) {
        setError(
          t('Attachment URL is required', {
            defaultValue: 'Attachment URL is required',
          })
        );
        return;
      }
    }

    setError(null);
    setIsSending(true);
    try {
      let response;
      if (mode === 'message') {
        response = await axios.post(`/secure-pay/${token}/dispute/message/`, {
          message: message.trim(),
        });
      } else {
        response = await axios.post(`/secure-pay/${token}/dispute/evidence/`, {
          title: evidenceTitle.trim(),
          message: evidenceComment.trim(),
          external_url: evidenceUrl.trim(),
          file: fileId,
        });
      }

      setTimeline([...timeline, response.data ])

      resetComposer();
      // onUpdated();
    } catch (e) {
      let fallback = t('Something went wrong! Please try again later.', {
        defaultValue: 'Something went wrong! Please try again later.',
      });
      if (axios.isAxiosError(e)) {
        const payload = e.response?.data as {message?: string} | undefined;
        if (payload?.message) fallback = payload.message;
      }
      setError(fallback);
    } finally {
      setIsSending(false);
    }
  };

  const onMessageKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mode !== 'message') return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className='d-flex flex-column min-h-0'>
      <DisputeChatInner
        isLoading={isLoading}
        timeline={timeline}
        mode={mode}
        message={message}
        evidenceTitle={evidenceTitle}
        evidenceComment={evidenceComment}
        evidenceUrl={evidenceUrl}
        isSending={isSending}
        error={error}
        onModeChange={setMode}
        onMessageChange={setMessage}
        onEvidenceTitleChange={setEvidenceTitle}
        onEvidenceCommentChange={setEvidenceComment}
        onEvidenceUrlChange={setEvidenceUrl}
        onEvidenceFileChange={(value) => {
          setFileId(Array.isArray(value) ? value[0] : value);
        }}
        onMessageKeyDown={onMessageKeyDown}
        onSubmit={submit}
        showFileUpload={true}
        view={'buyer'}
        footerClassName={'p-0'}
        editable={_dispute.status === 'dispute_opened'}
      />
    </div>
  );
}

export default PublicDisputeHistoryWidget;


